// Loan Compliance Module v1.0 PR 3 — drawer that records a Legal Ops
// check. A Pass without an EvidenceDocumentId is rejected client-side (the
// backend enforces the same rule); an Exception reveals severity / title /
// description fields that open a linked ComplianceException.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { documentsApi } from "@/api/documents";
import { foldersApi } from "@/api/folders";
import type {
  LegalOpsCheckOutcome,
  LegalOpsFailureReason,
  LegalOpsModule,
  RecordLegalOpsCheckRequest,
} from "@/api/compliance/types";
import { useRecordLegalOpsCheck } from "@/hooks/compliance/useCompliance";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { FAILURE_REASON_LABELS, metaForModule } from "./labels";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface RecordCheckDrawerProps {
  loanId: string;
  module: LegalOpsModule;
  /** Optional cadence-calendar item to pre-fill + auto-complete. */
  prefillCalendarItemId?: string | null;
  prefillPeriodLabel?: string | null;
  open: boolean;
  onClose: () => void;
}

const FAILURE_REASONS: LegalOpsFailureReason[] = [
  "InsuranceLapse",
  "KeyManInsuranceLapse",
  "UccContinuationOverdue",
  "UccPerfectionDefect",
  "DacaInactive",
  "MissingGoodStanding",
  "Other",
];

function defaultPeriodLabel(cadence: string, tz: string): string {
  // F10 — the period a check is FILED AGAINST is the viewer's current period.
  // Read in UTC, a filing at 17:00 Phoenix on Sep 30 defaulted to 2026-Q4 /
  // 2026-10 while Phoenix was still in Q3 / September. PeriodLabel is component
  // three of the (LoanId, Module, PeriodLabel) key that FinancialOpsCheck.cs:16
  // calls "the current state", so the wrong default files against the wrong row.
  const day = isoDateInTz(new Date(), tz); // YYYY-MM-DD in the viewer's zone
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7)); // 1-based
  switch (cadence) {
    case "Annual":
      return `${y}`;
    case "Quarterly": {
      const q = Math.floor((m - 1) / 3) + 1;
      return `${y}-Q${q}`;
    }
    case "Monthly":
      return `${y}-${String(m).padStart(2, "0")}`;
    default:
      return `${y}`;
  }
}

function isoToday(tz: string): string {
  return isoDateInTz(new Date(), tz);
}

export function RecordCheckDrawer({
  loanId,
  module,
  prefillCalendarItemId,
  prefillPeriodLabel,
  open,
  onClose,
}: RecordCheckDrawerProps) {
  const meta = metaForModule(module);
  const toast = useToast();
  const record = useRecordLegalOpsCheck(loanId);
  const tz = useUserTimeZone();

  const [outcome, setOutcome] = useState<LegalOpsCheckOutcome>("Pass");
  const [periodLabel, setPeriodLabel] = useState(
    prefillPeriodLabel || defaultPeriodLabel(meta.defaultCadence, tz),
  );
  const [asOfDate, setAsOfDate] = useState(isoToday(tz));
  const [cadence, setCadence] = useState(meta.defaultCadence);
  const [notes, setNotes] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceDocumentId, setEvidenceDocumentId] = useState<string | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);

  const [failureReason, setFailureReason] =
    useState<LegalOpsFailureReason>("Other");
  const [exceptionTitle, setExceptionTitle] = useState("");
  const [exceptionDescription, setExceptionDescription] = useState("");

  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOutcome("Pass");
    setPeriodLabel(prefillPeriodLabel || defaultPeriodLabel(meta.defaultCadence, tz));
    setAsOfDate(isoToday(tz));
    setCadence(meta.defaultCadence);
    setNotes("");
    setEvidenceFile(null);
    setEvidenceDocumentId(null);
    setFailureReason("Other");
    setExceptionTitle("");
    setExceptionDescription("");
    setFormError(null);
  }, [open, module, meta.defaultCadence, prefillPeriodLabel, tz]);

  const surfaceError = (err: unknown, fallback: string) => {
    const msg =
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback;
    setFormError(msg);
    toast.error(msg);
  };

  const handleFileChange = async (file: File | null) => {
    setFormError(null);
    setEvidenceFile(file);
    setEvidenceDocumentId(null);
    if (!file) return;
    setUploading(true);
    try {
      let tree;
      try {
        tree = await foldersApi.entityTree("loan", loanId);
      } catch {
        tree = await foldersApi.ensureEntityTree("loan", loanId, null);
      }
      const doc = await documentsApi.upload({
        title: file.name,
        category: "ComplianceDocument",
        folderId: tree.id,
        file,
        description: `Legal Ops evidence — ${meta.code}`,
        autoIngest: true,
      });
      setEvidenceDocumentId(doc.id);
    } catch (err) {
      setEvidenceFile(null);
      surfaceError(err, "Could not upload evidence.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setFormError(null);

    if (!periodLabel.trim()) {
      setFormError("Period label is required.");
      return;
    }
    if (!asOfDate) {
      setFormError("As-of date is required.");
      return;
    }
    if (outcome === "Pass" && !evidenceDocumentId) {
      setFormError("Evidence document is required for a Pass outcome.");
      return;
    }
    if (outcome === "Exception" && !exceptionTitle.trim()) {
      setFormError("Exception title is required.");
      return;
    }

    const body: RecordLegalOpsCheckRequest = {
      module,
      periodLabel: periodLabel.trim(),
      asOfDate,
      cadence: cadence.trim() || meta.defaultCadence,
      outcome,
      evidenceDocumentId: outcome === "Pass" ? evidenceDocumentId : null,
      notes: notes.trim() || null,
      relatedComplianceCalendarItemId: prefillCalendarItemId ?? null,
      failureReason: outcome === "Exception" ? failureReason : null,
      exceptionTitle: outcome === "Exception" ? exceptionTitle.trim() : null,
      exceptionDescription:
        outcome === "Exception" ? exceptionDescription.trim() || null : null,
    };

    try {
      await record.mutateAsync(body);
      toast.success(
        outcome === "Pass"
          ? "Legal Ops check recorded."
          : "Legal Ops exception opened.",
      );
      onClose();
    } catch (err) {
      surfaceError(err, "Could not record check.");
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Record check — ${meta.title}`}
      subtitle={`Module ${meta.code}`}
      testId="legal-ops-record-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={record.isPending || uploading}
            data-testid="legal-ops-record-submit"
          >
            {record.isPending ? "Saving…" : "Record check"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <fieldset className="flex gap-space-2" data-testid="legal-ops-outcome">
          <legend className="text-caption text-gray-mid uppercase tracking-label mb-space-1">
            Outcome
          </legend>
          <label className="flex items-center gap-space-1 text-small">
            <input
              type="radio"
              name="outcome"
              value="Pass"
              checked={outcome === "Pass"}
              onChange={() => setOutcome("Pass")}
              data-testid="legal-ops-outcome-pass"
            />
            Pass
          </label>
          <label className="flex items-center gap-space-1 text-small">
            <input
              type="radio"
              name="outcome"
              value="Exception"
              checked={outcome === "Exception"}
              onChange={() => setOutcome("Exception")}
              data-testid="legal-ops-outcome-exception"
            />
            Exception
          </label>
        </fieldset>

        <div className="grid grid-cols-2 gap-space-2">
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Period label
            </span>
            <input
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
              data-testid="legal-ops-period-label"
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">As-of</span>
            <input
              type="date"
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              data-testid="legal-ops-as-of"
            />
          </label>
          <label className="flex flex-col gap-space-1 text-caption">
            <span className="text-gray-mid uppercase tracking-label">
              Cadence
            </span>
            <input
              className="h-9 px-space-2 border border-border-light rounded-md text-small"
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              data-testid="legal-ops-cadence"
            />
          </label>
        </div>

        {outcome === "Pass" && (
          <div className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">
              Evidence document <span className="text-status-error">*</span>
            </span>
            <input
              type="file"
              onChange={(e) =>
                void handleFileChange(e.target.files?.[0] ?? null)
              }
              data-testid="legal-ops-evidence-file"
            />
            {uploading && (
              <p className="text-caption text-gray-mid m-0">Uploading…</p>
            )}
            {evidenceFile && evidenceDocumentId && (
              <p className="text-caption text-status-success m-0">
                Uploaded {evidenceFile.name}
              </p>
            )}
            <p className="text-caption text-gray-mid m-0">
              A Pass requires an evidence document. Upload one or paste an
              existing document ID.
            </p>
            <input
              type="text"
              placeholder="Existing document GUID (optional)"
              className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
              value={evidenceDocumentId ?? ""}
              onChange={(e) => setEvidenceDocumentId(e.target.value || null)}
              data-testid="legal-ops-evidence-doc-id"
            />
          </div>
        )}

        {outcome === "Exception" && (
          <div className="flex flex-col gap-space-2 rounded-md border-l-4 border-l-status-error bg-status-error/5 p-space-3">
            <p className="text-caption text-onyx m-0">
              An Exception opens a linked ComplianceException on this loan.
            </p>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Failure reason
              </span>
              <select
                className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
                value={failureReason}
                onChange={(e) =>
                  setFailureReason(e.target.value as LegalOpsFailureReason)
                }
                data-testid="legal-ops-failure-reason"
              >
                {FAILURE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {FAILURE_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Exception title
              </span>
              <input
                className="h-9 px-space-2 border border-border-light rounded-md text-small"
                value={exceptionTitle}
                onChange={(e) => setExceptionTitle(e.target.value)}
                data-testid="legal-ops-exception-title"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Description
              </span>
              <textarea
                className="min-h-[80px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                value={exceptionDescription}
                onChange={(e) => setExceptionDescription(e.target.value)}
                data-testid="legal-ops-exception-description"
              />
            </label>
          </div>
        )}

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Notes</span>
          <textarea
            className="min-h-[64px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="legal-ops-notes"
          />
        </label>

        {formError && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="legal-ops-form-error"
          >
            {formError}
          </p>
        )}
      </div>
    </ResourceDrawer>
  );
}
